import React, { useState, useEffect } from 'react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/Tabs';
import { EditableField } from '../ui/EditableField';
import { TagInput } from '../ui/TagInput';
import { SocialLinksInput } from '../ui/SocialLinksInput';
import { LocationPicker } from '../ui/LocationPicker';
import { MultiLocationPicker } from '../ui/MultiLocationPicker';
import { PitchSection } from '../ui/PitchSection';
import { PersonInput } from '../ui/PersonInput';
import { AssetInput } from '../ui/AssetInput';
import { StockInput } from '../ui/StockInput';
import FileUpload from '../ui/FileUpload';
import FinancialInput from '../ui/FinancialInput';
import { CompanyLogo } from './CompanyLogo';
import ProfessionalPDFExtractionButton from '../pdf/ProfessionalPDFExtractionButton';
import { useUser } from '../../hooks/useUser';
import { useCompany } from '../../hooks/useCompany';
import { useEntityVocabulary } from '../../hooks/useEntityVocabulary';
import { getProvinces, getCities } from '../../constants/jurisdictions';
import { customerApiService } from '../../services/customerApi';
import { Edit3, Save, X, Loader2, Scale, FolderOpen, Send, Building2, Info } from 'lucide-react';
import {
  getLegalFormsForVariant,
  COMPANY_TYPES,
  TRADITIONAL_SECTORS,
  STARTUP_SECTORS,
  TECHNOLOGY_READINESS_LEVELS,
  BUSINESS_MODELS,
  YES_NO_OPTIONS,
  CURRENCIES,
  getCompanySizesForVariant,
  ACCOMPANIMENT_TYPES
} from '../../constants/enterpriseFormOptions';

interface Coordinates {
  latitude: number;
  longitude: number;
  address?: string;
}

interface SocialLink {
  platform: string;
  url: string;
  label: string;
}

interface PitchData {
  elevator_pitch: string;
  value_proposition: string;
  target_market: string;
  competitive_advantage: string;
  pitch_deck_url?: string;
  demo_video_url?: string;
}

// Type de détenteur pour l'actionnariat
type HolderType = 'physique' | 'morale';

interface PersonData {
  id: string;
  // Type de détenteur (personne physique ou morale)
  typeDetenteur?: HolderType;
  
  // Champs personne physique
  nom: string;
  prenoms: string;
  fonction: string;
  nationalite?: string;
  telephone?: string;
  email?: string;
  adresse?: string;
  linkedin?: string;
  cv?: string; // URL du CV uploadé
  cvFileName?: string; // Nom du fichier CV
  
  // Champs personne morale
  raisonSociale?: string;
  formeJuridique?: string;
  rccm?: string; // Numéro RCCM
  idnat?: string; // ID National
  siegeSocial?: string;
  representantLegal?: string; // Nom du représentant légal
  representantFonction?: string; // Fonction du représentant
  
  // Champs communs actionnariat
  nombreActions?: number; // Nombre d'actions ou de parts sociales
  pourcentageActions?: number; // Pourcentage du capital
  
  // Autres champs
  dateNomination?: string;
  typeContrat?: string;
  salaire?: number;
  diplomes?: string[];
}

interface AssetData {
  id: string;
  designation: string;
  type: 'immobilier' | 'vehicule' | 'equipement' | 'stock' | 'autre';
  description?: string;
  
  // Valeurs financières détaillées
  prixAchat?: number; // Prix d'achat original
  valeurActuelle?: number; // Valeur actuelle estimée
  devise?: 'USD' | 'CDF' | 'EUR';
  
  // Informations temporelles
  dateAcquisition?: string; // Date d'acquisition
  
  // État et localisation
  etatActuel?: 'neuf' | 'excellent' | 'bon' | 'moyen' | 'mauvais' | 'deteriore';
  localisation?: string;
  
  // Informations techniques
  numeroSerie?: string;
  marque?: string;
  modele?: string;
  quantite?: number;
  unite?: string;
  
  // Statut de propriété
  proprietaire?: 'propre' | 'location' | 'leasing' | 'emprunt';
  
  // Observations
  observations?: string;
  
  // Champs hérités pour compatibilité
  valeur?: number; // Pour compatibilité descendante
  etatUsage?: 'neuf' | 'bon' | 'moyen' | 'mauvais'; // Pour compatibilité descendante
}

interface StockData {
  id: string;
  designation: string;
  categorie: 'matiere_premiere' | 'produit_semi_fini' | 'produit_fini' | 'fourniture' | 'emballage' | 'autre';
  description?: string;
  
  // Quantités et unités
  quantiteStock: number;
  unite: string;
  seuilMinimum?: number;
  seuilMaximum?: number;
  
  // Valeurs financières (actifs circulants)
  coutUnitaire: number;
  valeurTotaleStock: number;
  devise: 'USD' | 'CDF' | 'EUR';
  
  // Informations temporelles et rotation
  dateDernierInventaire?: string;
  dureeRotationMoyenne?: number;
  datePeremption?: string;
  
  // Localisation et stockage
  emplacement?: string;
  conditionsStockage?: string;
  
  // Suivi et gestion
  fournisseurPrincipal?: string;
  numeroLot?: string;
  codeArticle?: string;
  
  // État et observations
  etatStock: 'excellent' | 'bon' | 'moyen' | 'deteriore' | 'perime';
  observations?: string;
}

const EnterpriseIdentificationForm: React.FC = () => {
  const { user, isLoading: userLoading } = useUser();
  const { company, updateCompany, createCompany, loading: companyLoading } = useCompany(user?.companyId);
  // Vocabulaire adaptatif : entreprise / coopérative selon le variant courant.
  // Source : company.variant (backend) ou sessionStorage.signup_variant (onboarding).
  const vocab = useEntityVocabulary();
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ─────────────────────────────────────────────────────────────────────
  // Inline variant selector — visible TANT QUE le profil n'a pas été
  // soumis pour validation.
  // ─────────────────────────────────────────────────────────────────────
  // Bug fix : auparavant le selector n'apparaissait que si user.companyId
  // était null. Or customer-service.syncUser auto-crée silencieusement la
  // Company au premier /users/me, AVANT que l'utilisateur n'atteigne ce
  // form. user.companyId était donc déjà renseigné → selector caché →
  // variant resté 'standard' par défaut même pour les vrais coopératives.
  //
  // Nouveau critère : le selector reste visible tant que la company est
  // en brouillon (statutValidation = 'brouillon'). Une fois soumise pour
  // validation, le variant est verrouillé (PME = sme/standard,
  // Coopérative = sme/cooperative).
  const hasNoCompany = !user?.companyId || user.companyId.trim() === '';
  const isDraftCompany =
    company?.formulaireStatut?.statutValidation === 'brouillon' ||
    !company?.formulaireStatut?.statutValidation;
  const isNewAccount = hasNoCompany || isDraftCompany;
  // Lit l'état initial : priorité à company.variant si on en a un,
  // sinon sessionStorage (flow /register?variant=cooperative), sinon
  // 'standard'. Garantit que le toggle reflète toujours la vérité backend.
  const [pendingVariant, setPendingVariant] = useState<'standard' | 'cooperative'>(() => {
    const fromCompany = (company as any)?.variant;
    if (fromCompany === 'cooperative' || fromCompany === 'standard') return fromCompany;
    if (typeof window === 'undefined') return 'standard';
    try {
      const stored = sessionStorage.getItem('signup_variant');
      return stored === 'cooperative' ? 'cooperative' : 'standard';
    } catch {
      return 'standard';
    }
  });

  // Synchronise pendingVariant avec company.variant quand la company se
  // charge après le premier render (cas où le hook useCompany résout
  // asynchroniquement).
  useEffect(() => {
    const v = (company as any)?.variant;
    if ((v === 'cooperative' || v === 'standard') && v !== pendingVariant) {
      setPendingVariant(v);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company?.id, (company as any)?.variant]);

  const handleVariantChange = (next: 'standard' | 'cooperative') => {
    setPendingVariant(next);
    try {
      sessionStorage.setItem('signup_variant', next);
    } catch {
      // sessionStorage indisponible (privacy mode) — ignore, la valeur
      // sera quand même incluse dans le payload au submit via formData.
    }
    // Force un re-render du hook useCompanyVariant en émettant un storage
    // event manuel (les composants frères qui dépendent de useEntityVocabulary
    // se rafraîchissent ainsi sans prop drilling).
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new StorageEvent('storage', {
        key: 'signup_variant',
        newValue: next,
      }));
    }
  };
  
  // ✨ ANTI-VIBRATION: Ref pour tracker l'ID de l'entreprise déjà initialisée
  const initializedCompanyIdRef = React.useRef<string | null>(null);
  
  // État local pour le formulaire
  const [formData, setFormData] = useState({
    // Identification de base
    raisonSociale: '',
    sigle: '',
    formeJuridiqueOHADA: '',
    dateCreation: '',
    dateDebutActivites: '',
    numeroRCCM: '',
    numeroIdentificationNationale: '',
    numeroImpotFiscal: '',

    // Juridiction (localisation administrative) — complémentaire au GPS.
    // Persistée sur le profil CLIENT (Customer.address.province/city) pour
    // l'isolation régionale admin et le placement sur cartes. Optionnelle.
    province: '',
    ville: '',

    // Nature des activités
    typeEntreprise: 'traditional' as 'traditional' | 'startup',
    secteurActivitePrincipal: '',
    secteursActiviteSecondaires: [] as string[],
    secteursPersonalises: [] as string[], // Secteurs ajoutés par l'entreprise
    descriptionActivites: '',
    produitsServices: [] as string[],
    
    // Informations complémentaires
    tailleEntreprise: '',
    capitalSocial: '',
    deviseCapital: 'USD' as 'USD' | 'CDF' | 'EUR',
    
    // Incubation et accélération (optionnel)
    enIncubation: false,
    typeAccompagnement: '' as 'incubation' | 'acceleration' | '',
    nomIncubateurAccelerateur: '',
    certificatAffiliation: [] as any[], // Documents PDF
    
    // Coordonnées et localisation
    siegeSocial: null as Coordinates | null,
    siegeExploitation: null as Coordinates | null,
    unitesProduction: [] as Coordinates[],
    pointsVente: [] as Coordinates[],
    
    // Contacts et réseaux sociaux
    telephoneFixe: '',
    telephoneMobile: '',
    fax: '',
    email: '',
    boitePostale: '',
    reseauxSociaux: [] as SocialLink[],
    
    // Pitch et présentation
    pitch: {
      elevator_pitch: '',
      value_proposition: '',
      target_market: '',
      competitive_advantage: '',
      pitch_deck_url: '',
      demo_video_url: ''
    } as PitchData,
    
    // Spécificités startup
    niveauMaturiteTechnologique: '',
    modeleEconomique: '',
    proprieteIntellectuelle: [] as string[],
    
    // Spécificités traditionnelles
    certificationQualite: false,
    licencesExploitation: [] as string[],
    
    // Structure organisationnelle
    dirigeants: [] as PersonData[],
    employes: [] as PersonData[],
    actionnaires: [] as PersonData[],
    nombreEmployes: 0,
    organigramme: '',
    
    // Patrimoine et moyens
    immobilisations: [] as AssetData[],
    equipements: [] as AssetData[],
    vehicules: [] as AssetData[],
    stocks: [] as StockData[],
    moyensTechniques: [] as string[],
    capaciteProduction: '',
    
    // Financier et juridique
    comptesBancaires: [] as any[],
    assurances: [] as any[],
    financements: [] as any[],
    litigesEnCours: false,
    detailsLitiges: '',
    failliteAnterieure: false,
    detailsFaillite: '',
    poursuiteJudiciaire: false,
    detailsPoursuites: '',
    garantiePrets: false,
    detailsGaranties: '',
    antecedentesFiscales: false,
    detailsAntecedentes: '',
    
    // Documents obligatoires
    documentsEntreprise: [] as any[],
    documentsPersonnel: [] as any[],
    documentsFinanciers: [] as any[],
    documentsPatrimoine: [] as any[],
    documentsProprietéIntellectuelle: [] as any[],
    documentsSectoriels: [] as any[],
  });

  // ✨ SIMPLIFICATION: useEffect pour charger les données
  React.useEffect(() => {
    if (!company?.id || initializedCompanyIdRef.current === company.id) {
      return;
    }
    
    console.log('📥 Chargement des données dans le formulaire:', company.raisonSociale);
    initializedCompanyIdRef.current = company.id;
    
    // Helper pour copie profonde des tableaux d'objets (évite les mutations croisées)
    const deepCopyArray = <T extends object>(arr: T[] | undefined | null): T[] => {
      if (!arr || !Array.isArray(arr)) return [];
      return arr.map(item => ({ ...item }));
    };
    
    setFormData(prev => ({
      ...prev,
        // Identification de base
        raisonSociale: company.raisonSociale || '',
        sigle: company.sigle || '',
        formeJuridiqueOHADA: company.formeJuridiqueOHADA || '',
        dateCreation: company.dateCreation || '',
        dateDebutActivites: company.dateDebutActivites || '',
        numeroRCCM: company.numeroRCCM || '',
        numeroIdentificationNationale: company.numeroIdentificationNationale || '',
        numeroImpotFiscal: company.numeroImpotFiscal || '',

        // Juridiction (depuis l'adresse structurée du client si présente)
        province: (company as any)?.address?.province || '',
        ville: (company as any)?.address?.city || '',

        // Nature des activités
        typeEntreprise: company.typeEntreprise || 'traditional',
        secteurActivitePrincipal: company.secteurActivitePrincipal || '',
        secteursActiviteSecondaires: company.secteursActiviteSecondaires || [],
        secteursPersonalises: company.secteursPersonalises || [],
        descriptionActivites: company.descriptionActivites || '',
        produitsServices: company.produitsServices || [],

        // Informations complémentaires
        tailleEntreprise: company.tailleEntreprise || '',
        capitalSocial: company.capitalSocial || '',
        deviseCapital: company.deviseCapital || 'USD',

        // Incubation et accélération - COPIE PROFONDE
        enIncubation: company.enIncubation || false,
        typeAccompagnement: company.typeAccompagnement || '',
        nomIncubateurAccelerateur: company.nomIncubateurAccelerateur || '',
        certificatAffiliation: deepCopyArray(company.certificatAffiliation),
        
        // Coordonnées et localisation - COPIE PROFONDE pour tableaux d'objets
        siegeSocial: company.siegeSocial || null,
        siegeExploitation: company.siegeExploitation || null,
        unitesProduction: deepCopyArray(company.unitesProduction),
        pointsVente: deepCopyArray(company.pointsVente),
        
        // Contacts et réseaux sociaux - COPIE PROFONDE
        telephoneFixe: company.telephoneFixe || '',
        telephoneMobile: company.telephoneMobile || '',
        fax: company.fax || '',
        email: company.email || '',
        boitePostale: company.boitePostale || '',
        reseauxSociaux: deepCopyArray(company.reseauxSociaux),
        
        // Pitch et présentation
        pitch: company.pitch || {
          elevator_pitch: '',
          value_proposition: '',
          target_market: '',
          competitive_advantage: '',
          pitch_deck_url: '',
          demo_video_url: ''
        },
        
        // Spécificités startup - COPIE PROFONDE
        niveauMaturiteTechnologique: company.niveauMaturiteTechnologique || '',
        modeleEconomique: company.modeleEconomique || '',
        proprieteIntellectuelle: deepCopyArray(company.proprieteIntellectuelle),
        
        // Spécificités traditionnelles
        certificationQualite: company.certificationQualite || false,
        licencesExploitation: company.licencesExploitation || [],
        
        // Structure organisationnelle - COPIE PROFONDE pour éviter mutations croisées
        dirigeants: deepCopyArray(company.dirigeants),
        employes: deepCopyArray(company.employes),
        actionnaires: deepCopyArray(company.actionnaires),
        nombreEmployes: company.nombreEmployes || 0,
        organigramme: company.organigramme || '',
        
        // Patrimoine et moyens - COPIE PROFONDE
        immobilisations: deepCopyArray(company.immobilisations),
        equipements: deepCopyArray(company.equipements),
        vehicules: deepCopyArray(company.vehicules),
        stocks: deepCopyArray(company.stocks),
        moyensTechniques: company.moyensTechniques || [],
        capaciteProduction: company.capaciteProduction || '',
        
        // Financier et juridique - COPIE PROFONDE
        comptesBancaires: deepCopyArray(company.comptesBancaires),
        assurances: deepCopyArray(company.assurances),
        financements: deepCopyArray(company.financements),
        litigesEnCours: company.litigesEnCours || false,
        detailsLitiges: company.detailsLitiges || '',
        failliteAnterieure: company.failliteAnterieure || false,
        detailsFaillite: company.detailsFaillite || '',
        poursuiteJudiciaire: company.poursuiteJudiciaire || false,
        detailsPoursuites: company.detailsPoursuites || '',
        garantiePrets: company.garantiePrets || false,
        detailsGaranties: company.detailsGaranties || '',
        antecedentesFiscales: company.antecedentesFiscales || false,
        detailsAntecedentes: company.detailsAntecedentes || '',
        
        // Documents obligatoires - COPIE PROFONDE
        documentsEntreprise: deepCopyArray(company.documentsEntreprise),
        documentsPersonnel: deepCopyArray(company.documentsPersonnel),
        documentsFinanciers: deepCopyArray(company.documentsFinanciers),
        documentsPatrimoine: deepCopyArray(company.documentsPatrimoine),
        documentsProprietéIntellectuelle: deepCopyArray(company.documentsProprietéIntellectuelle),
        documentsSectoriels: deepCopyArray(company.documentsSectoriels),
      }));
    console.log('✅ Données initialisées dans le formulaire');
  }, [company?.id]); // Dépendance uniquement sur l'ID
  
  // ✨ Calculer l'état d'initialisation
  const isFormInitialized = !user?.companyId || initializedCompanyIdRef.current === company?.id;

  // Gestion du mode édition
  const handleStartEditing = () => setIsEditing(true);
  
  const handleCancelEditing = () => {
    setIsEditing(false);
    // Restaurer TOUTES les données depuis company
    if (company) {
      console.log('🔄 Restauration des données originales depuis le backend');
      
      setFormData(prev => ({
        ...prev,
        // Identification de base
        raisonSociale: company.raisonSociale || '',
        sigle: company.sigle || '',
        formeJuridiqueOHADA: company.formeJuridiqueOHADA || '',
        dateCreation: company.dateCreation || '',
        dateDebutActivites: company.dateDebutActivites || '',
        numeroRCCM: company.numeroRCCM || '',
        numeroIdentificationNationale: company.numeroIdentificationNationale || '',
        numeroImpotFiscal: company.numeroImpotFiscal || '',

        // Juridiction (restaure depuis l'adresse structurée du client)
        province: (company as any)?.address?.province || '',
        ville: (company as any)?.address?.city || '',

        // Nature des activités
        typeEntreprise: company.typeEntreprise || 'traditional',
        secteurActivitePrincipal: company.secteurActivitePrincipal || '',
        secteursActiviteSecondaires: company.secteursActiviteSecondaires || [],
        secteursPersonalises: company.secteursPersonalises || [],
        descriptionActivites: company.descriptionActivites || '',
        produitsServices: company.produitsServices || [],

        // Informations complémentaires
        tailleEntreprise: company.tailleEntreprise || '',
        capitalSocial: company.capitalSocial || '',
        deviseCapital: company.deviseCapital || 'USD',
        
        // Contacts
        telephoneFixe: company.telephoneFixe || '',
        telephoneMobile: company.telephoneMobile || '',
        fax: company.fax || '',
        email: company.email || '',
        boitePostale: company.boitePostale || '',
        
        // ... tous les autres champs
      }));
    }
  };

  const handleSaveProfile = async () => {
    setIsSaving(true);
    try {
      // ✨ NOUVEAU: Transmettre TOUTES les données du formulaire - pas de mapping complexe
      // Le formulaire est la source de vérité, on envoie tout directement
      const updateData: any = {
        // ============================================
        // SECTION: Identification de Base
        // ============================================
        raisonSociale: formData.raisonSociale,
        sigle: formData.sigle,
        formeJuridiqueOHADA: formData.formeJuridiqueOHADA,
        dateCreation: formData.dateCreation,
        dateDebutActivites: formData.dateDebutActivites,
        numeroRCCM: formData.numeroRCCM,
        numeroIdentificationNationale: formData.numeroIdentificationNationale,
        numeroImpotFiscal: formData.numeroImpotFiscal,
        
        // ============================================
        // SECTION: Nature des Activités
        // ============================================
        typeEntreprise: formData.typeEntreprise,
        secteurActivitePrincipal: formData.secteurActivitePrincipal,
        secteursActiviteSecondaires: formData.secteursActiviteSecondaires,
        secteursPersonalises: formData.secteursPersonalises,
        descriptionActivites: formData.descriptionActivites,
        produitsServices: formData.produitsServices,
        
        // ============================================
        // SECTION: Informations Complémentaires
        // ============================================
        tailleEntreprise: formData.tailleEntreprise,
        capitalSocial: formData.capitalSocial,
        deviseCapital: formData.deviseCapital,
        
        // ============================================
        // SECTION: Incubation et Accélération
        // ============================================
        enIncubation: formData.enIncubation,
        typeAccompagnement: formData.typeAccompagnement,
        nomIncubateurAccelerateur: formData.nomIncubateurAccelerateur,
        certificatAffiliation: formData.certificatAffiliation,
        
        // ============================================
        // SECTION: Coordonnées et Localisation
        // ============================================
        siegeSocial: formData.siegeSocial,
        siegeExploitation: formData.siegeExploitation,
        unitesProduction: formData.unitesProduction,
        pointsVente: formData.pointsVente,
        
        // ============================================
        // SECTION: Contacts et Réseaux Sociaux
        // ============================================
        telephoneFixe: formData.telephoneFixe,
        telephoneMobile: formData.telephoneMobile,
        fax: formData.fax,
        email: formData.email,
        boitePostale: formData.boitePostale,
        reseauxSociaux: formData.reseauxSociaux,
        
        // ============================================
        // SECTION: Pitch et Présentation
        // ============================================
        pitch: formData.pitch,
        
        // ============================================
        // SECTION: Spécificités Startup
        // ============================================
        niveauMaturiteTechnologique: formData.niveauMaturiteTechnologique,
        modeleEconomique: formData.modeleEconomique,
        proprieteIntellectuelle: formData.proprieteIntellectuelle,
        
        // ============================================
        // SECTION: Spécificités Traditionnelles
        // ============================================
        certificationQualite: formData.certificationQualite,
        licencesExploitation: formData.licencesExploitation,
        
        // ============================================
        // SECTION: Structure Organisationnelle
        // ============================================
        dirigeants: formData.dirigeants,
        employes: formData.employes,
        actionnaires: formData.actionnaires,
        nombreEmployes: formData.nombreEmployes,
        organigramme: formData.organigramme,
        
        // ============================================
        // SECTION: Patrimoine et Moyens
        // ============================================
        immobilisations: formData.immobilisations,
        equipements: formData.equipements,
        vehicules: formData.vehicules,
        stocks: formData.stocks,
        moyensTechniques: formData.moyensTechniques,
        capaciteProduction: formData.capaciteProduction,
        
        // ============================================
        // SECTION: Financier et Juridique
        // ============================================
        comptesBancaires: formData.comptesBancaires,
        assurances: formData.assurances,
        financements: formData.financements,
        litigesEnCours: formData.litigesEnCours,
        detailsLitiges: formData.detailsLitiges,
        failliteAnterieure: formData.failliteAnterieure,
        detailsFaillite: formData.detailsFaillite,
        poursuiteJudiciaire: formData.poursuiteJudiciaire,
        detailsPoursuites: formData.detailsPoursuites,
        garantiePrets: formData.garantiePrets,
        detailsGaranties: formData.detailsGaranties,
        antecedentesFiscales: formData.antecedentesFiscales,
        detailsAntecedentes: formData.detailsAntecedentes,
        
        // ============================================
        // SECTION: Documents Obligatoires
        // ============================================
        documentsEntreprise: formData.documentsEntreprise,
        documentsPersonnel: formData.documentsPersonnel,
        documentsFinanciers: formData.documentsFinanciers,
        documentsPatrimoine: formData.documentsPatrimoine,
        documentsProprietéIntellectuelle: formData.documentsProprietéIntellectuelle,
        documentsSectoriels: formData.documentsSectoriels,
      };
      
      await updateCompany(updateData);
      setIsEditing(false);
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSubmitProfile = async () => {
    setIsSubmitting(true);
    try {
      // Variant orthogonal au type — résolu par ordre de priorité :
      //  1. pendingVariant (selector inline pour nouveau compte)
      //  2. sessionStorage.signup_variant (flow /register?variant=cooperative)
      //  3. null → backend default 'standard'
      // Le backend traite la coopérative comme une PME (type='sme') et ne
      // consulte `variant` que pour le pricing et le vocabulaire UI.
      const sessionVariant = (typeof window !== 'undefined'
        ? sessionStorage.getItem('signup_variant')
        : null) as 'standard' | 'cooperative' | null;
      // Pour un nouveau compte, le selector est la source canonique. Pour
      // une company existante on n'envoie pas variant (le backend garde
      // celui en DB), évitant d'écraser par accident.
      const signupVariant: 'standard' | 'cooperative' | null = isNewAccount
        ? pendingVariant
        : sessionVariant;

      // ✨ NOUVEAU: Transmettre TOUTES les données + marquer comme soumis
      const updateData: any = {
        // ============================================
        // TOUTES LES DONNÉES DU FORMULAIRE
        // ============================================
        ...(signupVariant ? { variant: signupVariant } : {}),
        raisonSociale: formData.raisonSociale,
        sigle: formData.sigle,
        formeJuridiqueOHADA: formData.formeJuridiqueOHADA,
        dateCreation: formData.dateCreation,
        dateDebutActivites: formData.dateDebutActivites,
        numeroRCCM: formData.numeroRCCM,
        numeroIdentificationNationale: formData.numeroIdentificationNationale,
        numeroImpotFiscal: formData.numeroImpotFiscal,
        typeEntreprise: formData.typeEntreprise,
        secteurActivitePrincipal: formData.secteurActivitePrincipal,
        secteursActiviteSecondaires: formData.secteursActiviteSecondaires,
        secteursPersonalises: formData.secteursPersonalises,
        descriptionActivites: formData.descriptionActivites,
        produitsServices: formData.produitsServices,
        tailleEntreprise: formData.tailleEntreprise,
        capitalSocial: formData.capitalSocial,
        deviseCapital: formData.deviseCapital,
        enIncubation: formData.enIncubation,
        typeAccompagnement: formData.typeAccompagnement,
        nomIncubateurAccelerateur: formData.nomIncubateurAccelerateur,
        certificatAffiliation: formData.certificatAffiliation,
        siegeSocial: formData.siegeSocial,
        siegeExploitation: formData.siegeExploitation,
        unitesProduction: formData.unitesProduction,
        pointsVente: formData.pointsVente,
        telephoneFixe: formData.telephoneFixe,
        telephoneMobile: formData.telephoneMobile,
        fax: formData.fax,
        email: formData.email,
        boitePostale: formData.boitePostale,
        reseauxSociaux: formData.reseauxSociaux,
        pitch: formData.pitch,
        niveauMaturiteTechnologique: formData.niveauMaturiteTechnologique,
        modeleEconomique: formData.modeleEconomique,
        proprieteIntellectuelle: formData.proprieteIntellectuelle,
        certificationQualite: formData.certificationQualite,
        licencesExploitation: formData.licencesExploitation,
        dirigeants: formData.dirigeants,
        employes: formData.employes,
        actionnaires: formData.actionnaires,
        nombreEmployes: formData.nombreEmployes,
        organigramme: formData.organigramme,
        immobilisations: formData.immobilisations,
        equipements: formData.equipements,
        vehicules: formData.vehicules,
        stocks: formData.stocks,
        moyensTechniques: formData.moyensTechniques,
        capaciteProduction: formData.capaciteProduction,
        comptesBancaires: formData.comptesBancaires,
        assurances: formData.assurances,
        financements: formData.financements,
        litigesEnCours: formData.litigesEnCours,
        detailsLitiges: formData.detailsLitiges,
        failliteAnterieure: formData.failliteAnterieure,
        detailsFaillite: formData.detailsFaillite,
        poursuiteJudiciaire: formData.poursuiteJudiciaire,
        detailsPoursuites: formData.detailsPoursuites,
        garantiePrets: formData.garantiePrets,
        detailsGaranties: formData.detailsGaranties,
        antecedentesFiscales: formData.antecedentesFiscales,
        detailsAntecedentes: formData.detailsAntecedentes,
        documentsEntreprise: formData.documentsEntreprise,
        documentsPersonnel: formData.documentsPersonnel,
        documentsFinanciers: formData.documentsFinanciers,
        documentsPatrimoine: formData.documentsPatrimoine,
        documentsProprietéIntellectuelle: formData.documentsProprietéIntellectuelle,
        documentsSectoriels: formData.documentsSectoriels,
        
        // ============================================
        // Marquer comme soumis pour validation
        // ============================================
        formulaireStatut: {
          estComplete: true,
          derniereMiseAJour: new Date().toISOString(),
          etapesCompletees: ['general'],
          prochainEtapes: ['validation'],
          documentsJoints: [],
          scoreCompletion: 100,
          statutValidation: 'soumis',
          datesoumission: new Date().toISOString(),
        },
      };
      
      await updateCompany(updateData);

      // ── Juridiction → profil CLIENT (customer-service) ──────────────────
      // Persiste Ville/Province sur Customer.address pour l'isolation régionale
      // admin + le placement sur cartes. Complémentaire au GPS. NON bloquant :
      // un échec ici ne doit pas invalider la sauvegarde de l'entreprise.
      const jurisdictionCustomerId =
        user?.customerId || (company as any)?.customerId || undefined;
      if ((formData.province || formData.ville) && jurisdictionCustomerId) {
        try {
          await customerApiService.updateJurisdiction(jurisdictionCustomerId, {
            city: formData.ville || undefined,
            province: formData.province || undefined,
          });
          console.log('✅ Juridiction (Ville/Province) persistée sur le profil client');
        } catch (jurisdictionError) {
          console.warn(
            '⚠️ Échec de la persistance de la juridiction sur le profil client (non bloquant):',
            jurisdictionError,
          );
        }
      }

      setIsEditing(false);

      // TODO: Envoyer notification à l'administration Wanzo
      console.log('Profil soumis pour validation par l\'administration Wanzo');

    } catch (error) {
      console.error('Erreur lors de la soumission:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Gestion des champs individuels
  const handleFieldChange = async (field: string, value: any) => {
    if (!isEditing) return;
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Options sectorielles dynamiques
  const getSecteurOptions = () => {
    return formData.typeEntreprise === 'startup' ? STARTUP_SECTORS : TRADITIONAL_SECTORS;
  };

  // ✨ ANTI-VIBRATION: Afficher un loader stable pendant le chargement initial
  // Conditions claires: user en chargement OU (companyId existe mais pas encore chargé)
  const isInitialLoading = userLoading || (user?.companyId && !company && companyLoading);
  
  // ✨ ANTI-VIBRATION: Attendre que les données soient prêtes avant d'afficher le formulaire
  // Si l'utilisateur a une entreprise, attendre qu'elle soit chargée ET initialisée
  const waitingForData = user?.companyId && !isFormInitialized;
  
  if (isInitialLoading || waitingForData) {
    return (
      <div className="max-w-6xl mx-auto p-4 sm:p-6">
        <div className="flex flex-col items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-[#197ca8]" />
          <p className="mt-4 text-gray-600">Chargement du formulaire...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6">
      {/* Toggle PME / Coopérative — uniquement pour les nouveaux comptes,
          avant création de la company en DB. Une fois la company créée,
          son variant prime via le backend et ce toggle disparaît. */}
      {isNewAccount && (
        <div className="mb-4 inline-grid grid-cols-2 gap-1 rounded-md bg-gray-100 p-1 w-72">
          <button
            type="button"
            onClick={() => handleVariantChange('standard')}
            className={`px-3 py-2 text-xs sm:text-sm font-semibold text-center rounded-md transition-colors ${
              pendingVariant === 'standard'
                ? 'bg-white text-primary shadow-sm'
                : 'bg-transparent text-gray-600 hover:bg-gray-200'
            }`}
          >
            PME
          </button>
          <button
            type="button"
            onClick={() => handleVariantChange('cooperative')}
            className={`px-3 py-2 text-xs sm:text-sm font-semibold text-center rounded-md transition-colors ${
              pendingVariant === 'cooperative'
                ? 'bg-white text-primary shadow-sm'
                : 'bg-transparent text-gray-600 hover:bg-gray-200'
            }`}
          >
            Coopérative
          </button>
        </div>
      )}

      {/* En-tête avec boutons d'édition */}
      <div className="flex flex-col space-y-4 lg:flex-row lg:items-center lg:justify-between lg:space-y-0 mb-6 pb-4 border-b border-gray-200">
        <div className="min-w-0 flex-1">
          <h1 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-2">
            {vocab.formTitle}
          </h1>
          <p className="text-sm sm:text-base text-gray-600">
            Informations détaillées sur {vocab.yourEntity}
          </p>
          {companyLoading && isFormInitialized && (
            <div className="flex items-center text-sm text-[#197ca8] mt-1">
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
              Synchronisation...
            </div>
          )}
        </div>
        
        <div className="flex items-center space-x-2 sm:space-x-3 flex-shrink-0">
          {!isEditing ? (
            <button
              onClick={handleStartEditing}
              className="inline-flex items-center px-3 py-2 sm:px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#197ca8] hover:bg-[#156a92] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#197ca8] transition-colors"
            >
              <Edit3 className="w-4 h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Éditer le profil</span>
              <span className="sm:hidden">Éditer</span>
            </button>
          ) : (
            <>
              <button
                onClick={handleCancelEditing}
                disabled={isSaving || isSubmitting}
                className="inline-flex items-center px-3 py-2 sm:px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#197ca8] transition-colors disabled:opacity-50"
              >
                <X className="w-4 h-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Annuler</span>
              </button>
              <button
                onClick={handleSaveProfile}
                disabled={isSaving || isSubmitting}
                className="inline-flex items-center px-3 py-2 sm:px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors disabled:opacity-50"
              >
                {isSaving ? (
                  <Loader2 className="w-4 h-4 mr-1 sm:mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-1 sm:mr-2" />
                )}
                {isSaving ? 'Sauvegarde...' : 'Sauvegarder'}
              </button>
              <button
                onClick={handleSubmitProfile}
                disabled={isSaving || isSubmitting}
                className="inline-flex items-center px-3 py-2 sm:px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors disabled:opacity-50"
              >
                {isSubmitting ? (
                  <Loader2 className="w-4 h-4 mr-1 sm:mr-2 animate-spin" />
                ) : (
                  <Send className="w-4 h-4 mr-1 sm:mr-2" />
                )}
                {isSubmitting ? 'Soumission...' : 'Soumettre'}
              </button>
            </>
          )}
        </div>
      </div>

      <Tabs defaultValue="general">
        <TabsList className="mb-8">
          <TabsTrigger value="general">Général</TabsTrigger>
          <TabsTrigger value="patrimoine">Patrimoine</TabsTrigger>
          <TabsTrigger value="structure">Structure</TabsTrigger>
          <TabsTrigger value="financier">Finance & Juridique</TabsTrigger>
          <TabsTrigger value="localisation">Localisation</TabsTrigger>
          <TabsTrigger value="presentation">Pitch</TabsTrigger>
        </TabsList>

        {/* Section I: Renseignements Généraux */}
        <TabsContent value="general">
          <div className="space-y-8">
            {/* 1. Identification de base */}
            <section className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">
                1. Identification de Base
              </h2>
              
              {/* Logo de l'entité */}
              <div className="mb-6 pb-6 border-b border-gray-200">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  {vocab.logoLabel}
                </label>
                {/* Si l'entité a un UUID valide, utiliser CompanyLogo */}
                {company?.id && !company.id.includes('temp') ? (
                  <CompanyLogo
                    companyId={company.id}
                    logo={company?.logo}
                    name={formData.raisonSociale || (vocab.variant === 'cooperative' ? 'Votre Coopérative' : 'Votre Entreprise')}
                    onLogoUpdate={(success) => {
                      if (success) {
                        console.log('Logo mis à jour avec succès');
                      }
                    }}
                  />
                ) : (
                  <div className="space-y-3">
                    <div className="w-24 h-24 sm:w-32 sm:h-32 rounded-full bg-gray-200 flex items-center justify-center">
                      <Building2 className="w-12 h-12 text-gray-400" />
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <Info className="w-4 h-4 text-blue-500" />
                      <span>{vocab.saveBeforeLogo}</span>
                    </div>
                  </div>
                )}
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <EditableField
                  label="Raison sociale ou dénomination *"
                  value={formData.raisonSociale}
                  onSave={(value) => handleFieldChange('raisonSociale', value)}
                  type="text"
                  required
                  disabled={!isEditing}
                />
                <EditableField
                  label="Sigle"
                  value={formData.sigle}
                  onSave={(value) => handleFieldChange('sigle', value)}
                  type="text"
                  disabled={!isEditing}
                />
                <EditableField
                  label="Forme juridique selon l'OHADA *"
                  value={formData.formeJuridiqueOHADA}
                  onSave={(value) => handleFieldChange('formeJuridiqueOHADA', value)}
                  type="select"
                  options={getLegalFormsForVariant(vocab.variant) as any}
                  required
                  disabled={!isEditing}
                />
                <EditableField
                  label="Date de création *"
                  value={formData.dateCreation}
                  onSave={(value) => handleFieldChange('dateCreation', value)}
                  type="date"
                  required
                  disabled={!isEditing}
                />
                <EditableField
                  label="Date de début effectif des activités"
                  value={formData.dateDebutActivites}
                  onSave={(value) => handleFieldChange('dateDebutActivites', value)}
                  type="date"
                  disabled={!isEditing}
                />
                <EditableField
                  label={vocab.sizeLabel}
                  value={formData.tailleEntreprise}
                  onSave={(value) => handleFieldChange('tailleEntreprise', value)}
                  type="select"
                  options={getCompanySizesForVariant(vocab.variant) as any}
                  disabled={!isEditing}
                />
              </div>
            </section>

            {/* 2. Immatriculations */}
            <section className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">
                2. Immatriculations et Identifications
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <EditableField
                  label="Numéro RCCM"
                  value={formData.numeroRCCM}
                  onSave={(value) => handleFieldChange('numeroRCCM', value)}
                  type="text"
                  placeholder="CD/KIN/RCCM/YY-X-NNNNN"
                  disabled={!isEditing}
                />
                <EditableField
                  label="Numéro d'identification nationale"
                  value={formData.numeroIdentificationNationale}
                  onSave={(value) => handleFieldChange('numeroIdentificationNationale', value)}
                  type="text"
                  placeholder="NAT/KIN/YYYY/NNNNN"
                  disabled={!isEditing}
                />
                <EditableField
                  label="Numéro d'impôt fiscal"
                  value={formData.numeroImpotFiscal}
                  onSave={(value) => handleFieldChange('numeroImpotFiscal', value)}
                  type="text"
                  disabled={!isEditing}
                />
              </div>
            </section>

            {/* 2 bis. Juridiction (localisation administrative) — optionnelle.
                Province + Ville (RDC par défaut). Complémentaire aux coordonnées
                GPS de l'onglet Localisation : ces valeurs sont persistées sur le
                profil CLIENT (Customer.address.province/city) et alimentent
                l'isolation régionale des administrateurs + le placement sur cartes. */}
            <section className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">
                Juridiction (localisation administrative)
              </h2>
              <p className="text-sm text-gray-500 mb-4">
                Province et ville de rattachement (facultatif). Permet de situer
                {vocab.variant === 'cooperative' ? ' la coopérative' : " l'entreprise"} sur les cartes
                et l'isolation régionale, même sans coordonnées GPS précises.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <EditableField
                  label="Province"
                  value={formData.province}
                  onSave={(value) => {
                    if (!isEditing) return Promise.resolve();
                    // Changer de province réinitialise la ville (listes liées).
                    return new Promise<void>((resolve) => {
                      setFormData(prev => ({
                        ...prev,
                        province: String(value),
                        ville: '',
                      }));
                      setTimeout(() => resolve(), 0);
                    });
                  }}
                  type="select"
                  options={getProvinces().map(p => ({ value: p.value, label: p.label }))}
                  disabled={!isEditing}
                />
                <EditableField
                  label="Ville"
                  value={formData.ville}
                  onSave={(value) => handleFieldChange('ville', value)}
                  type="select"
                  options={getCities(formData.province).map(c => ({ value: c, label: c }))}
                  disabled={!isEditing || !formData.province}
                  placeholder={formData.province ? 'Sélectionner...' : "Choisir d'abord une province"}
                />
              </div>
            </section>

            {/* 3. Nature des activités */}
            <section className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">
                3. Nature des Activités
              </h2>
              
              <div className="space-y-6">
                {/* Toggle "Type d'entreprise" (traditional/startup) — concept
                    PME-only. Pour une coopérative, ce choix n'a pas de sens :
                    une coopérative est ni "traditionnelle" ni "startup" au
                    sens entrepreneurial du terme. On hide pour les coops ;
                    typeEntreprise reste implicitement à 'traditional' en DB
                    pour compat (les secteurs TRADITIONAL_SECTORS suffisent). */}
                {vocab.variant !== 'cooperative' && (
                  <EditableField
                    label="Type d'entreprise *"
                    value={formData.typeEntreprise}
                    onSave={async (value) => {
                      if (!isEditing) return Promise.resolve();
                      const newType = value as 'traditional' | 'startup';

                      // Déterminer les secteurs disponibles pour le nouveau type
                      const newSectorOptions = newType === 'startup' ? STARTUP_SECTORS : TRADITIONAL_SECTORS;
                      const currentSector = formData.secteurActivitePrincipal;

                      // Vérifier si le secteur actuel existe dans les nouvelles options
                      const sectorExists = newSectorOptions.some(opt => opt.value === currentSector);

                      return new Promise<void>((resolve) => {
                        setFormData(prev => ({
                          ...prev,
                          typeEntreprise: newType,
                          // Réinitialiser le secteur si incompatible avec le nouveau type
                          secteurActivitePrincipal: sectorExists ? currentSector : '',
                          // Réinitialiser aussi les secteurs secondaires si incompatibles
                          secteursActiviteSecondaires: sectorExists ? prev.secteursActiviteSecondaires : []
                        }));
                        // Laisser React mettre à jour l'état avant de résoudre
                        setTimeout(() => resolve(), 0);
                      });
                    }}
                    type="select"
                    options={COMPANY_TYPES as any}
                    required
                    disabled={!isEditing}
                  />
                )}

                <EditableField
                  label="Secteur d'activité principal *"
                  value={formData.secteurActivitePrincipal}
                  onSave={(value) => handleFieldChange('secteurActivitePrincipal', value)}
                  type="select"
                  options={getSecteurOptions() as any}
                  required
                  disabled={!isEditing}
                />

                <TagInput
                  label="Secteurs d'activité secondaires"
                  value={formData.secteursActiviteSecondaires}
                  onSave={(tags) => handleFieldChange('secteursActiviteSecondaires', tags)}
                  disabled={!isEditing}
                  placeholder="Ajoutez des secteurs secondaires"
                  suggestions={getSecteurOptions().map(opt => opt.label)}
                />

                <div>
                  <TagInput
                    label="Secteurs personnalisés"
                    value={formData.secteursPersonalises}
                    onSave={(tags) => handleFieldChange('secteursPersonalises', tags)}
                    disabled={!isEditing}
                    placeholder="Ajoutez vos propres secteurs d'activité"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Vous pouvez ajouter des secteurs qui ne sont pas dans notre liste prédéfinie
                  </p>
                </div>

                <EditableField
                  label="Description détaillée des activités *"
                  value={formData.descriptionActivites}
                  onSave={(value) => handleFieldChange('descriptionActivites', value)}
                  type="textarea"
                  required
                  disabled={!isEditing}
                />

                <TagInput
                  label="Produits/Services offerts"
                  value={formData.produitsServices}
                  onSave={(tags) => handleFieldChange('produitsServices', tags)}
                  disabled={!isEditing}
                  placeholder="Ajoutez vos produits et services"
                />
              </div>
            </section>

            {/* 4. Capital social / Capital variable */}
            {/* En droit OHADA des coopératives, le capital est "variable" et
                représenté par des "parts sociales" — pas un capital social
                figé comme dans les sociétés commerciales. On adapte uniquement
                le libellé, la donnée reste stockée dans le même champ. */}
            <section className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">
                4. {vocab.variant === 'cooperative'
                  ? 'Capital Variable et Parts Sociales'
                  : 'Capital Social'}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <EditableField
                  label={vocab.variant === 'cooperative'
                    ? 'Montant du capital variable'
                    : 'Montant du capital social'}
                  value={formData.capitalSocial}
                  onSave={(value) => handleFieldChange('capitalSocial', value)}
                  type="number"
                  disabled={!isEditing}
                />
                <EditableField
                  label="Devise"
                  value={formData.deviseCapital}
                  onSave={(value) => handleFieldChange('deviseCapital', value)}
                  type="select"
                  options={CURRENCIES as any}
                  disabled={!isEditing}
                />
              </div>
            </section>

            {/* 5. Incubation et Accélération (optionnel) — masqué pour les
                coopératives : incubateurs/accélérateurs sont des dispositifs
                d'écosystème startup. Une coopérative de détaillants/artisans
                ne participe jamais à un parcours d'incubation. */}
            {vocab.variant !== 'cooperative' && (
            <section className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">
                5. Incubation et Accélération (optionnel)
              </h2>
              <p className="text-sm text-gray-600 mb-6">
                Si {vocab.yourEntity} bénéficie d'un accompagnement par un incubateur ou accélérateur, veuillez renseigner ces informations.
              </p>
              
              <div className="space-y-6">
                <EditableField
                  label="Votre entreprise est-elle actuellement en incubation ou accélération ?"
                  value={formData.enIncubation ? 'oui' : 'non'}
                  onSave={async (value) => {
                    if (!isEditing) return Promise.resolve();
                    const isIncubated = value === 'oui';
                    return new Promise<void>((resolve) => {
                      setFormData(prev => ({ 
                        ...prev, 
                        enIncubation: isIncubated,
                        // Reset des champs si pas en incubation
                        typeAccompagnement: isIncubated ? prev.typeAccompagnement : '',
                        nomIncubateurAccelerateur: isIncubated ? prev.nomIncubateurAccelerateur : '',
                        certificatAffiliation: isIncubated ? prev.certificatAffiliation : []
                      }));
                      // Laisser React mettre à jour l'état avant de résoudre
                      setTimeout(() => resolve(), 0);
                    });
                  }}
                  type="select"
                  options={YES_NO_OPTIONS as any}
                  disabled={!isEditing}
                />

                {formData.enIncubation && (
                  <>
                    <EditableField
                      label="Type d'accompagnement"
                      value={formData.typeAccompagnement}
                      onSave={(value) => handleFieldChange('typeAccompagnement', value)}
                      type="select"
                      options={ACCOMPANIMENT_TYPES as any}
                      disabled={!isEditing}
                    />

                    <EditableField
                      label="Nom de l'incubateur ou accélérateur"
                      value={formData.nomIncubateurAccelerateur}
                      onSave={(value) => handleFieldChange('nomIncubateurAccelerateur', value)}
                      type="text"
                      disabled={!isEditing}
                      placeholder="ex: CTIC Dakar, I&P, etc."
                    />

                    <FileUpload
                      label="Certificat d'affiliation"
                      description="Certificat ou attestation d'affiliation à l'incubateur/accélérateur (PDF)"
                      value={formData.certificatAffiliation}
                      onSave={(files) => handleFieldChange('certificatAffiliation', files)}
                      disabled={!isEditing}
                      multiple={false}
                      acceptedTypes={['.pdf']}
                      maxSize={5}
                    />
                  </>
                )}
              </div>
            </section>
            )}

            {/* Questions spécifiques selon le type */}
            {formData.typeEntreprise === 'startup' && (
              <section className="bg-blue-50 rounded-lg border border-blue-200 p-6">
                <h2 className="text-lg font-medium text-blue-900 mb-4">
                  Spécificités Startup Technologique
                </h2>
                <div className="space-y-6">
                  <EditableField
                    label="Niveau de maturité technologique (TRL)"
                    value={formData.niveauMaturiteTechnologique}
                    onSave={(value) => handleFieldChange('niveauMaturiteTechnologique', value)}
                    type="select"
                    options={TECHNOLOGY_READINESS_LEVELS as any}
                    disabled={!isEditing}
                  />

                  <EditableField
                    label="Modèle économique"
                    value={formData.modeleEconomique}
                    onSave={(value) => handleFieldChange('modeleEconomique', value)}
                    type="select"
                    options={BUSINESS_MODELS as any}
                    disabled={!isEditing}
                  />

                  <TagInput
                    label="Propriété intellectuelle"
                    value={formData.proprieteIntellectuelle}
                    onSave={(tags) => handleFieldChange('proprieteIntellectuelle', tags)}
                    disabled={!isEditing}
                    placeholder="Brevets, marques, droits d'auteur..."
                    suggestions={['Brevet', 'Marque déposée', 'Droit d\'auteur', 'Secret commercial', 'Design industriel']}
                  />
                </div>
              </section>
            )}

            {formData.typeEntreprise === 'traditional' && (
              <section className="bg-green-50 rounded-lg border border-green-200 p-6">
                <h2 className="text-lg font-medium text-green-900 mb-4">
                  {vocab.variant === 'cooperative'
                    ? 'Certifications et Licences'
                    : 'Spécificités Entreprise Traditionnelle'}
                </h2>
                <div className="space-y-6">
                  <EditableField
                    label="Certification qualité"
                    value={formData.certificationQualite ? 'oui' : 'non'}
                    onSave={(value) => handleFieldChange('certificationQualite', value === 'oui')}
                    type="select"
                    options={YES_NO_OPTIONS as any}
                    disabled={!isEditing}
                  />

                  <TagInput
                    label="Licences d'exploitation spécifiques"
                    value={formData.licencesExploitation}
                    onSave={(tags) => handleFieldChange('licencesExploitation', tags)}
                    disabled={!isEditing}
                    placeholder="Licences sectorielles, autorisations..."
                    suggestions={['Licence d\'importation', 'Autorisation sanitaire', 'Permis d\'exploitation', 'Certification ISO']}
                  />
                </div>
              </section>
            )}
          </div>
        </TabsContent>

        {/* Section II: Localisation & Contacts */}
        <TabsContent value="localisation">
          <div className="space-y-8">
            {/* Localisation */}
            <section className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">
                1. Localisation des Sites
              </h2>
              <div className="space-y-6">
                <LocationPicker
                  label="Siège social *"
                  value={formData.siegeSocial}
                  onSave={(coords) => handleFieldChange('siegeSocial', coords)}
                  disabled={!isEditing}
                />
                
                <LocationPicker
                  label="Siège d'exploitation"
                  value={formData.siegeExploitation}
                  onSave={(coords) => handleFieldChange('siegeExploitation', coords)}
                  disabled={!isEditing}
                />

                <MultiLocationPicker
                  label="Unités de production"
                  description="Localisations des sites de production, usines, ateliers, etc."
                  value={formData.unitesProduction}
                  onChange={(locations) => handleFieldChange('unitesProduction', locations)}
                  disabled={!isEditing}
                  maxLocations={5}
                  placeholder="Ajouter une unité de production"
                />

                <MultiLocationPicker
                  label="Points de vente"
                  description="Localisations des magasins, boutiques, showrooms, bureaux commerciaux, etc."
                  value={formData.pointsVente}
                  onChange={(locations) => handleFieldChange('pointsVente', locations)}
                  disabled={!isEditing}
                  maxLocations={10}
                  placeholder="Ajouter un point de vente"
                />
              </div>
            </section>

            {/* Contacts */}
            <section className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">
                2. Informations de Contact
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <EditableField
                  label="Téléphone fixe"
                  value={formData.telephoneFixe}
                  onSave={(value) => handleFieldChange('telephoneFixe', value)}
                  type="tel"
                  disabled={!isEditing}
                />
                <EditableField
                  label="Téléphone mobile"
                  value={formData.telephoneMobile}
                  onSave={(value) => handleFieldChange('telephoneMobile', value)}
                  type="tel"
                  disabled={!isEditing}
                />
                <EditableField
                  label="Fax"
                  value={formData.fax}
                  onSave={(value) => handleFieldChange('fax', value)}
                  type="tel"
                  disabled={!isEditing}
                />
                <EditableField
                  label="Email"
                  value={formData.email}
                  onSave={(value) => handleFieldChange('email', value)}
                  type="email"
                  disabled={!isEditing}
                />
                <EditableField
                  label="Boîte postale"
                  value={formData.boitePostale}
                  onSave={(value) => handleFieldChange('boitePostale', value)}
                  type="text"
                  disabled={!isEditing}
                />
              </div>
            </section>

            {/* Réseaux sociaux */}
            <section className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">
                3. Présence Numérique
              </h2>
              <SocialLinksInput
                label="Site web et réseaux sociaux"
                value={formData.reseauxSociaux}
                onSave={(links) => handleFieldChange('reseauxSociaux', links)}
                disabled={!isEditing}
              />
            </section>
          </div>
        </TabsContent>

        {/* Section III: Pitch & Présentation */}
        <TabsContent value="presentation">
          <div className="space-y-8">
            <section className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">
                {vocab.pitchTitle}
              </h2>
              <PitchSection
                label="Présentation commerciale"
                value={formData.pitch}
                onSave={(pitch) => handleFieldChange('pitch', pitch)}
                disabled={!isEditing}
              />
            </section>
          </div>
        </TabsContent>

        {/* Sections restantes - maintenant implémentées */}
        <TabsContent value="patrimoine">
          <div className="space-y-8">
            {/* Immobilisations */}
            <section className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">
                1. Immobilisations
              </h2>
              <AssetInput
                label="Biens immobiliers"
                value={formData.immobilisations}
                onSave={(assets) => handleFieldChange('immobilisations', assets)}
                disabled={!isEditing}
                assetType="immobilier"
                placeholder="Aucun bien immobilier enregistré"
              />
            </section>

            {/* Équipements */}
            <section className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">
                2. Équipements et Matériels
              </h2>
              <AssetInput
                label="Équipements de production et matériels"
                value={formData.equipements}
                onSave={(assets) => handleFieldChange('equipements', assets)}
                disabled={!isEditing}
                assetType="equipement"
                placeholder="Aucun équipement enregistré"
              />
            </section>

            {/* Véhicules */}
            <section className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">
                3. Parc Automobile
              </h2>
              <AssetInput
                label="Véhicules et moyens de transport"
                value={formData.vehicules}
                onSave={(assets) => handleFieldChange('vehicules', assets)}
                disabled={!isEditing}
                assetType="vehicule"
                placeholder="Aucun véhicule enregistré"
              />
            </section>

            {/* Stocks */}
            <section className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">
                4. Stocks et Inventaires (Actifs Circulants)
              </h2>
              <StockInput
                label="Stocks de matières premières et produits finis"
                value={formData.stocks}
                onSave={(stocks) => handleFieldChange('stocks', stocks)}
                disabled={!isEditing}
                placeholder="Aucun stock enregistré"
              />
            </section>

            {/* Moyens techniques */}
            <section className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">
                5. Moyens Techniques et Capacités
              </h2>
              <div className="space-y-6">
                <TagInput
                  label="Moyens techniques et technologiques"
                  value={formData.moyensTechniques}
                  onSave={(tags) => handleFieldChange('moyensTechniques', tags)}
                  disabled={!isEditing}
                  placeholder="Logiciels, systèmes, outils techniques..."
                  suggestions={['ERP', 'CRM', 'Site web', 'Application mobile', 'Base de données', 'Système de sécurité']}
                />

                <EditableField
                  label="Capacité de production"
                  value={formData.capaciteProduction}
                  onSave={(value) => handleFieldChange('capaciteProduction', value)}
                  type="textarea"
                  disabled={!isEditing}
                  placeholder="Décrivez vos capacités de production actuelles et potentielles"
                />
              </div>
            </section>
          </div>
        </TabsContent>

        <TabsContent value="structure">
          <div className="space-y-8">
            {/* Dirigeants */}
            <section className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">
                1. Équipe Dirigeante
              </h2>
              <PersonInput
                label="Dirigeants et responsables"
                value={formData.dirigeants}
                onSave={(persons) => handleFieldChange('dirigeants', persons)}
                disabled={!isEditing}
                personType="dirigeant"
                placeholder="Aucun dirigeant enregistré"
                companyType={formData.typeEntreprise}
              />
            </section>

            {/* Actionnaires — masqué pour les coopératives. En droit OHADA
                des sociétés coopératives, on parle de "membres associés" /
                "coopérants" (déjà gérés via le module BusinessUnit côté
                accounting), pas d'actionnaires détenant du capital. La
                section Dirigeants ci-dessus suffit à représenter la
                gouvernance d'une coopérative. */}
            {vocab.variant !== 'cooperative' && (
            <section className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">
                2. Structure Actionnariale
              </h2>
              <PersonInput
                label="Actionnaires et associés"
                value={formData.actionnaires}
                onSave={(persons) => handleFieldChange('actionnaires', persons)}
                disabled={!isEditing}
                personType="actionnaire"
                showEquity={true}
                formeJuridique={formData.formeJuridiqueOHADA}
                placeholder="Aucun actionnaire enregistré"
                companyType={formData.typeEntreprise}
              />
            </section>
            )}

            {/* Personnel — pour une coopérative, ce sont les "coopérants" et
                non les "employés". On adapte uniquement le libellé via vocab. */}
            <section className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">
                3. {vocab.variant === 'cooperative' ? 'Coopérants et Personnel' : 'Personnel et Employés'}
              </h2>
              <div className="space-y-6">
                <EditableField
                  label={`Nombre total ${vocab.variant === 'cooperative' ? 'de coopérants' : "d'employés"}`}
                  value={formData.nombreEmployes.toString()}
                  onSave={async (value) => {
                    if (!isEditing) return;
                    setFormData(prev => ({ ...prev, nombreEmployes: parseInt(value.toString()) || 0 }));
                  }}
                  type="number"
                  disabled={!isEditing}
                />

                <PersonInput
                  label="Employés clés"
                  value={formData.employes}
                  onSave={(persons) => handleFieldChange('employes', persons)}
                  disabled={!isEditing}
                  personType="employe"
                  showContract={true}
                  placeholder="Aucun employé clé enregistré"
                  companyType={formData.typeEntreprise}
                />
              </div>
            </section>

            {/* Organigramme */}
            <section className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">
                4. Organisation Interne
              </h2>
              <EditableField
                label="Description de l'organigramme"
                value={formData.organigramme}
                onSave={(value) => handleFieldChange('organigramme', value)}
                type="textarea"
                disabled={!isEditing}
                placeholder="Décrivez la structure organisationnelle de votre entreprise"
              />
            </section>
          </div>
        </TabsContent>

        <TabsContent value="financier">
          <div className="space-y-8">
            {/* Informations bancaires */}
            <section className="bg-white rounded-lg border border-gray-200 p-6">
              <FinancialInput
                label="Références Bancaires"
                type="bank-accounts"
                value={formData.comptesBancaires}
                onSave={(accounts) => handleFieldChange('comptesBancaires', accounts)}
                disabled={!isEditing}
                companyType={formData.typeEntreprise}
              />
            </section>

            {/* Financements traditionnels */}
            <section className="bg-white rounded-lg border border-gray-200 p-6">
              <FinancialInput
                label="Concours Financiers et Prêts"
                type="loans"
                value={formData.financements}
                onSave={(loans) => handleFieldChange('financements', loans)}
                disabled={!isEditing}
                companyType={formData.typeEntreprise}
              />
            </section>

            {/* Levées de fonds pour startups */}
            {formData.typeEntreprise === 'startup' && (
              <section className="bg-white rounded-lg border border-gray-200 p-6">
                <FinancialInput
                  label="Levées de Fonds"
                  type="funding-rounds"
                  value={formData.financements}
                  onSave={(rounds) => handleFieldChange('financements', rounds)}
                  disabled={!isEditing}
                  companyType={formData.typeEntreprise}
                />
              </section>
            )}

            {/* Aspects juridiques */}
            <section className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-6 flex items-center">
                <Scale className="text-red-500 mr-2 h-5 w-5" />
                Aspects Juridiques et Réglementaires
              </h2>
              
              <div className="space-y-6">
                {/* Faillite ou insolvabilité */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <EditableField
                    label="Avez-vous déjà fait faillite ou préparez-vous une demande de faillite/insolvabilité ?"
                    value={formData.failliteAnterieure ? 'oui' : 'non'}
                    onSave={async (value) => {
                      if (!isEditing) return;
                      setFormData(prev => ({ ...prev, failliteAnterieure: value === 'oui' }));
                    }}
                    type="select"
                    options={YES_NO_OPTIONS as any}
                    disabled={!isEditing}
                  />
                  
                  {formData.failliteAnterieure && (
                    <div className="mt-4">
                      <EditableField
                        label="Détails de la faillite"
                        value={formData.detailsFaillite}
                        onSave={async (value) => {
                          if (!isEditing) return;
                          setFormData(prev => ({ ...prev, detailsFaillite: value.toString() }));
                        }}
                        type="textarea"
                        disabled={!isEditing}
                        placeholder="Décrivez les circonstances et l'état actuel..."
                      />
                    </div>
                  )}
                </div>

                {/* Poursuites judiciaires */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <EditableField
                    label="Vous-même ou votre entreprise faites-vous l'objet de poursuites judiciaires ?"
                    value={formData.poursuiteJudiciaire ? 'oui' : 'non'}
                    onSave={async (value) => {
                      if (!isEditing) return;
                      setFormData(prev => ({ ...prev, poursuiteJudiciaire: value === 'oui' }));
                    }}
                    type="select"
                    options={YES_NO_OPTIONS as any}
                    disabled={!isEditing}
                  />
                  
                  {formData.poursuiteJudiciaire && (
                    <div className="mt-4">
                      <EditableField
                        label="Détails des poursuites"
                        value={formData.detailsPoursuites}
                        onSave={async (value) => {
                          if (!isEditing) return;
                          setFormData(prev => ({ ...prev, detailsPoursuites: value.toString() }));
                        }}
                        type="textarea"
                        disabled={!isEditing}
                        placeholder="Nature des poursuites, état d'avancement, montants en jeu..."
                      />
                    </div>
                  )}
                </div>

                {/* Garanties de prêts */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <EditableField
                    label="Êtes-vous actuellement garant d'un ou plusieurs prêts ?"
                    value={formData.garantiePrets ? 'oui' : 'non'}
                    onSave={async (value) => {
                      if (!isEditing) return;
                      setFormData(prev => ({ ...prev, garantiePrets: value === 'oui' }));
                    }}
                    type="select"
                    options={YES_NO_OPTIONS as any}
                    disabled={!isEditing}
                  />
                  
                  {formData.garantiePrets && (
                    <div className="mt-4">
                      <EditableField
                        label="Détails des garanties"
                        value={formData.detailsGaranties}
                        onSave={async (value) => {
                          if (!isEditing) return;
                          setFormData(prev => ({ ...prev, detailsGaranties: value.toString() }));
                        }}
                        type="textarea"
                        disabled={!isEditing}
                        placeholder="Montants garantis, bénéficiaires, échéances..."
                      />
                    </div>
                  )}
                </div>

                {/* Antécédents fiscaux */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <EditableField
                    label="Avez-vous eu des antécédents avec l'administration fiscale ?"
                    value={formData.antecedentesFiscales ? 'oui' : 'non'}
                    onSave={async (value) => {
                      if (!isEditing) return;
                      setFormData(prev => ({ ...prev, antecedentesFiscales: value === 'oui' }));
                    }}
                    type="select"
                    options={YES_NO_OPTIONS as any}
                    disabled={!isEditing}
                  />
                  
                  {formData.antecedentesFiscales && (
                    <div className="mt-4">
                      <EditableField
                        label="Détails des antécédents fiscaux"
                        value={formData.detailsAntecedentes}
                        onSave={async (value) => {
                          if (!isEditing) return;
                          setFormData(prev => ({ ...prev, detailsAntecedentes: value.toString() }));
                        }}
                        type="textarea"
                        disabled={!isEditing}
                        placeholder="Nature des problèmes, résolution, situation actuelle..."
                      />
                    </div>
                  )}
                </div>
              </div>
            </section>

            {/* Documents obligatoires */}
            <section className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-6 flex items-center">
                <FolderOpen className="text-blue-500 mr-2 h-5 w-5" />
                Documents Obligatoires
              </h2>
              
              <div className="space-y-8">
                {/* Documents entreprise */}
                <div>
                  <FileUpload
                    label="Documents Entreprise"
                    description="Statuts, RCCM, autorisations, attestations fiscales"
                    value={formData.documentsEntreprise}
                    onSave={(files) => handleFieldChange('documentsEntreprise', files)}
                    disabled={!isEditing}
                    multiple={true}
                    acceptedTypes={['.pdf', '.doc', '.docx', '.jpg', '.png']}
                    maxSize={10}
                  />
                </div>

                {/* Documents personnel */}
                <div>
                  <FileUpload
                    label="Documents Personnel"
                    description="CV des dirigeants, cartes d'identité, procurations"
                    value={formData.documentsPersonnel}
                    onSave={(files) => handleFieldChange('documentsPersonnel', files)}
                    disabled={!isEditing}
                    multiple={true}
                    acceptedTypes={['.pdf', '.doc', '.docx', '.jpg', '.png']}
                    maxSize={5}
                  />
                </div>

                {/* Documents financiers */}
                <div>
                  <FileUpload
                    label={formData.typeEntreprise === 'startup' ? 'Documents Financiers et Business Plan' : 'États Financiers'}
                    description={
                      formData.typeEntreprise === 'startup' 
                        ? 'Business plan, projections financières, pitch deck, term sheets'
                        : 'États financiers certifiés des 3 derniers exercices (format OHADA)'
                    }
                    value={formData.documentsFinanciers}
                    onSave={(files) => handleFieldChange('documentsFinanciers', files)}
                    disabled={!isEditing}
                    multiple={true}
                    acceptedTypes={['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx']}
                    maxSize={20}
                  />
                </div>

                {/* Documents patrimoine */}
                <div>
                  <FileUpload
                    label="Documents Patrimoine et Équipements"
                    description="Factures d'équipements, contrats de licence, certificats de propriété"
                    value={formData.documentsPatrimoine}
                    onSave={(files) => handleFieldChange('documentsPatrimoine', files)}
                    disabled={!isEditing}
                    multiple={true}
                    acceptedTypes={['.pdf', '.doc', '.docx', '.jpg', '.png']}
                    maxSize={10}
                  />
                </div>

                {/* Documents propriété intellectuelle (surtout startups) */}
                {formData.typeEntreprise === 'startup' && (
                  <div>
                    <FileUpload
                      label="Documents Propriété Intellectuelle"
                      description="Brevets, marques, NDA, accords de transfert IP"
                      value={formData.documentsProprietéIntellectuelle}
                      onSave={(files) => handleFieldChange('documentsProprietéIntellectuelle', files)}
                      disabled={!isEditing}
                      multiple={true}
                      acceptedTypes={['.pdf', '.doc', '.docx']}
                      maxSize={10}
                    />
                  </div>
                )}

                {/* Documents sectoriels */}
                <div>
                  <FileUpload
                    label="Documents Sectoriels Spécifiques"
                    description="Licences sectorielles, certifications qualité, autorisations spécialisées"
                    value={formData.documentsSectoriels}
                    onSave={(files) => handleFieldChange('documentsSectoriels', files)}
                    disabled={!isEditing}
                    multiple={true}
                    acceptedTypes={['.pdf', '.doc', '.docx', '.jpg', '.png']}
                    maxSize={15}
                  />
                </div>
              </div>
            </section>
          </div>
        </TabsContent>
      </Tabs>

      {/* Section d'extraction PDF */}
      <div className="mt-8 pt-6 border-t border-gray-200">
        <div className="bg-gray-50 rounded-lg p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div className="mb-4 sm:mb-0">
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Extraction du formulaire
              </h3>
              <p className="text-sm text-gray-600">
                Téléchargez votre formulaire d'identification au format PDF pour archivage ou soumission externe.
              </p>
            </div>
            <div className="flex-shrink-0">
              <ProfessionalPDFExtractionButton
                type="company"
                formData={formData}
                entityName={formData.raisonSociale || company?.raisonSociale || "Mon entreprise"}
                disabled={false}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnterpriseIdentificationForm;